import React, { useContext, useState, Dispatch, SetStateAction } from 'react';
import { ISearchParams, TripType } from './searchParams';
import { days, Days } from 'common/utils';
import { DateFormat } from 'common/types';

enum OrderType {
  fareDesc = 'frc',
  sdateDesc = 'sdd',
}

export enum PeriodType {
  MAX = 15,
  MIN = 1,
}

export interface IInfoCity {
  code: string;
  name: string;
  isDomestic?: boolean;
}

interface IInfoSearchParams {
  isDirect: boolean;
  isDomestic: boolean;
  period: number;
  sdate?: string;
  arrivalCity: IInfoCity;
  departureCity: IInfoCity;
  departureDate: string;
  isProductChange?: boolean;
}

interface IContextValue {
  infoSearchParams: IInfoSearchParams;
  setInfoSearchParams: Dispatch<SetStateAction<IInfoSearchParams>>;
  setInfoParamsFromCommon: Dispatch<ISearchParams>;
}

const defaultInfoSearchParams = {
  isDirect: false,
  isDomestic: false,
  period: 3,
  departureCity: {
    name: '인천',
    code: 'ICN',
  },
  arrivalCity: {
    name: '',
    code: '',
  },
  departureDate: '',
  isProductChange: false,
};

const InfoParamsContext = React.createContext<IContextValue>({
  infoSearchParams: defaultInfoSearchParams,
  setInfoSearchParams: () => {},
  setInfoParamsFromCommon: () => {},
});

interface IProps {
  searchParams?: ISearchParams;
  children: React.ReactNode;
}

const getPeriod = (searchParams: ISearchParams) => {
  const dep = searchParams.itinerary?.[0]?.departureDate;
  const arr = searchParams.itinerary?.[1]?.departureDate;
  if (dep && arr) {
    const diff = days(arr).diff(dep, 'day') + 1;
    return diff < PeriodType.MIN
      ? PeriodType.MIN
      : diff > PeriodType.MAX
      ? PeriodType.MAX
      : diff;
  }
  return 3;
};

// issues/1861 pc 메인 날짜 없을때 기본 날짜. 국내는 오늘, 해외는 오늘+1
const getDefaultDepartureDate = (searchParams: ISearchParams) => {
  const dep = searchParams.itinerary?.[0]?.departureCity?.name;
  const arr = searchParams.itinerary?.[0]?.arrivalCity?.name;
  if (!dep || !arr) {
    return '';
  }
  const isDepDomestic =
    searchParams.itinerary?.[0]?.departureCity?.isDomestic || false;
  const isArrDomestic =
    searchParams.itinerary?.[0]?.arrivalCity?.isDomestic || false;
  return isDepDomestic && isArrDomestic
    ? days(new Date()).format(DateFormat.YMD)
    : days(new Date()).add(1, 'day').format(DateFormat.YMD);
};

function InfoParamsContextProvider({ searchParams, children }: IProps) {
  const [infoSearchParams, setInfoSearchParams] = useState<IInfoSearchParams>(
    defaultInfoSearchParams,
  );

  const setInfoParamsFromCommon = (searchParams: ISearchParams) => {
    const [itinerary] = searchParams.itinerary;
    if (!itinerary.arrivalCity || !itinerary.departureCity) return;
    const { arrivalCity, departureCity, departureDate } = itinerary;
    setInfoSearchParams({
      ...defaultInfoSearchParams,
      isDirect: searchParams.isDirect,
      arrivalCity,
      departureCity,
      period: getPeriod(searchParams),
      departureDate: departureDate || getDefaultDepartureDate(searchParams),
    });
  };

  return (
    <InfoParamsContext.Provider
      value={{
        infoSearchParams,
        setInfoSearchParams,
        setInfoParamsFromCommon,
      }}
    >
      {children}
    </InfoParamsContext.Provider>
  );
}

function useInfoParamsContext() {
  return useContext(InfoParamsContext);
}

export {
  InfoParamsContextProvider,
  useInfoParamsContext,
  InfoParamsContext,
  OrderType,
};
